import React, { useState, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  LinearProgress,
} from "@mui/material";
import { ReportContext } from "../../ReportContext";
import { uploadMedia } from "../../../utils/MediaUploader";
import { showSwalNotification } from "../../../utils/SwalResponse";
import Swal from "sweetalert2";
import { AiOutlineCloudUpload } from "react-icons/ai";
import { MdOutlinePreview } from "react-icons/md";
import SimpleTiptapEditor from "../../../components/tiptap-templates/simple/custom-editor";

const Implemented = ({ solutionId, reload, setReload }) => {
  const { setLoading, implementedSolution } = useContext(ReportContext);
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(
    localStorage.getItem(`implementation_notes_${solutionId}`) || ""
  );
  const [mediaUrl, setMediaUrl] = useState(
    localStorage.getItem(`implementation_media_url_${solutionId}`) || ""
  );
  // const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // open/close dialog
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  

  // Save notes to localStorage
  const handleNotesChange = (htmlContent) => {
    
    setNotes(htmlContent);
    localStorage.setItem(`implementation_notes_${solutionId}`, htmlContent);
  };

  // Handle file upload immediately after selection
  const handleFileChange = async (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    // setFile(selected);
    setUploading(true);
    // setLoading(true);

    const uploadResult = await uploadMedia(selected);
    if (uploadResult?.url) {
      setMediaUrl(uploadResult.url);
      localStorage.setItem(
        `implementation_media_url_${solutionId}`,
        uploadResult.url
      );
      // Swal.fire({
      //   icon: "success",
      //   title: "Media Uploaded",
      //   text: "Your file has been successfully uploaded.",
      //   confirmButtonColor: "#4CAF50",
      //   timer: 1500,
      //   showConfirmButton: false,
      // });
    } else {
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: "Please try uploading the file again.",
      });
    }

    setUploading(false);
    setLoading(false);
  };

  // Submit implementation
  const handleSubmit = async () => {
    if (!notes.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Implementation notes cannot be empty",
        confirmButtonColor: "#f44336",
      });
      return;
    }

    if (!mediaUrl) {
      Swal.fire({
        icon: "warning",
        title: "Please upload a photo or video first",
        confirmButtonColor: "#f44336",
      });
      return;
    }

    setSubmitting(true);
    setLoading(true);

    const response = await implementedSolution({
      solution_id: solutionId,
      notes,
      media_url: mediaUrl,
    });

    setSubmitting(false);
    setLoading(false);

    if (response?.status === "success") {
      // clear localStorage
      localStorage.removeItem(`implementation_notes_${solutionId}`);
      localStorage.removeItem(`implementation_media_url_${solutionId}`);
      setNotes("");
      // setFile(null);
      setMediaUrl("");

      Swal.fire({
        icon: "success",
        title: "Submission Successful",
        text: "Your implementation details have been recorded.",
        confirmButtonColor: "#4CAF50",
      });

      if (setReload) setReload(!reload);
      handleClose();
    } else {
      showSwalNotification(response);
    }
  };

  return (
    <>
      {/* Trigger Button */}
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpen}
        sx={{ textTransform: "none", fontWeight: 600 }}
      >
        Implemented
      </Button>

      {/* Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            zIndex: 1700, // 👈 Higher z-index to appear above others
          },
        }}
        sx={{
          "& .MuiBackdrop-root": { zIndex: 1699 },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "primary.main" }}>
          Record Implementation
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: "#fafafa" }}>
          {/* Notes */}
          <Typography variant="subtitle1" fontWeight={600} mb={1}>
            Implementation Notes
          </Typography>
          {/* <TextField
            multiline
            fullWidth
            minRows={5}
            value={notes}
            onChange={handleNotesChange}
            placeholder="Type your implementation notes here..."
            variant="outlined"
          /> */}

          <SimpleTiptapEditor
            setContent={handleNotesChange}
            initialContent={notes || "<p>Type your implementation notes here...</p>"}
            navbarHeight={60}
            debounceDelay={500}
          />

          {/* Media Upload */}
          <Box mt={3}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Attach Proof (Photo or Video)
            </Typography>

            <Button
              component="label"
              variant="outlined"
              startIcon={<AiOutlineCloudUpload />}
              sx={{ textTransform: "none", fontWeight: 600, mb: 2 }}
              disabled={uploading}
            >
              {uploading ? "Uploading..." : "Upload File"}
              <input
                type="file"
                accept="image/*,video/*"
                hidden
                onChange={handleFileChange}
              />
            </Button>

            {uploading && <LinearProgress color="info" sx={{ mb: 2 }} />}

            {/* Preview */}
            {mediaUrl && (
              <Box
                mt={1}
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: 2,
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  bgcolor: "#fff",
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
                >
                  <MdOutlinePreview /> Preview
                </Typography>

                {mediaUrl.match(/\.(mp4|mov|avi|webm)$/i) ? (
                  <video
                    src={mediaUrl}
                    controls
                    style={{
                      width: "auto",
                      height: "100%",
                      maxHeight: 320,
                      borderRadius: 8,
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <img
                    src={mediaUrl}
                    alt="Preview"
                    style={{
                      height: "100%",
                      maxHeight: 320,
                      borderRadius: 8,
                      objectFit: "cover",
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleClose} color="inherit" disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="success"
            variant="contained"
            disabled={submitting || !mediaUrl || !notes}
          >
            {submitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Implemented;
